import "./style.css";
import { forwardRef, useEffect, useMemo, useRef, useState } from "react";
import { FormInput } from "components/Inputs";
import CactusNotifyIcon from "assets/icons/cactus_notify_icon.svg?react";
import CactusSaddleIcon from "assets/icons/cactus_saddleIcon.svg?react";
import CarouselCards from "components/CarouselCards";
import { gql, useMutation, useQuery } from "@apollo/client";
import {
  LIST_DISCIPLINES,
  LIST_DISCIPLINES_QUERY_OPTIONS,
} from "apollo/queries/disciplines";
import { listUserHorses } from "apollo/queries/horses";
import { formatListArray, getByDisciplineFilterValues, seatSizes } from "utils";
import { useAuthStore } from "stores/auth/authStore";
import Loader from "components/Spinners/Loader";
import RangeSlider from "components/RangeSlider";
import Button from "components/Buttons";
import { FormProvider, useForm } from "react-hook-form";
import { useLocation, useNavigate } from "react-router-dom";
import {
  CREATE_SADDLE_FAVORITE,
  DELETE_SADDLE_FAVORITE,
} from "apollo/mutations/saddleFavorite";

import { LIST_SADDLE_FAVORITES } from "apollo/queries/saddleFavorite";
import { GET_SADDLES_QUERY } from "apollo/queries/getCactusSaddles";
import ModalStore from "components/Modal/ModalStore";
import { useCartStore } from "stores/cart/useCartStore";
import { Modal, useModalStore } from "stores/modal/useModalStore";
import { useBrandDisciplineStore } from "stores/brandDiscipline/useBrandDisciplineStore";
import SingleSelectField from "components/ui/single-select";
import MultiSelectField from "components/Checkbox/HorseProfile/HorseMultiSelect";
import { LIST_MANUFACTURERS } from "apollo/queries/listManufacturers";
import { formatName } from "utils/index";

/** Sentinel value for the guest horse dropdown; opens login instead of selecting a horse. */
const HORSE_SELECT_LOGIN_VALUE = "__sf_login_to_select_horse__";

const SaddleSelectionControls = forwardRef(({ isCactus, isBillyCook }, ref) => {
  const isExclusiveBrandPage = Boolean(isCactus || isBillyCook);
  const cardsRef = useRef(null);
  const subdomain = useAuthStore((state) => state.domain);
  const brandName = useAuthStore((state) => state.brandName);
  const minPriceLimit = 1500;
  const maxPriceLimit = 10000;
  const arrivalsOptions = [
    { value: "BEST_FIT_SCORE", label: "Best Fit Score" },
    { value: "NEW_ARRIVALS", label: "New Arrivals" },
    { value: "PRICE_HIGH", label: "Price: High to Low" },
    { value: "PRICE_LOW", label: "Price: Low to High" },
  ];
  const navigate = useNavigate();
  const location = useLocation();
  const open = useModalStore((s) => s.open);

  const { data: listManufacturersData } = useQuery(LIST_MANUFACTURERS);

  const brandOptions =
    listManufacturersData?.listManufacturers?.map((manufacturer) => ({
      value: manufacturer.id,
      name: manufacturer.brand,
      label: formatName(manufacturer.brand),
    })) || [];

  const handleAddToCart = useCartStore((action) => action.handleAddToCart);
  const addToCartLoading = useCartStore(
    (state) => state.loadingState?.addToCartLoading
  );
  const changeActionState = useBrandDisciplineStore(
    (action) => action.changeActionState
  );

  const selectedBrandName = useBrandDisciplineStore(
    (state) => state.selectedBrandName
  );
  const selectedDiscipline = useBrandDisciplineStore(
    (state) => state.selectedDiscipline
  );
  const setSelectedBrandName = useBrandDisciplineStore(
    (action) => action.setSelectedBrandName
  );
  const setSelectedDiscipline = useBrandDisciplineStore(
    (action) => action.setSelectedDiscipline
  );

  const methods = useForm({
    defaultValues: {
      minPrice: `$${minPriceLimit.toLocaleString("en-US")}`,
      maxPrice: `$${maxPriceLimit.toLocaleString("en-US")}`,
      currentMinPrice: `$${minPriceLimit.toLocaleString("en-US")}`,
      currentMaxPrice: `$${maxPriceLimit.toLocaleString("en-US")}`,
    },
  });

  const { setValue, watch } = methods;

  const minPrice =
    parseInt(watch("minPrice")?.replace(/,/g, "").replace(/\$/g, ""), 10) ||
    minPriceLimit;
  const maxPrice =
    parseInt(watch("maxPrice")?.replace(/,/g, "").replace(/\$/g, ""), 10) ||
    maxPriceLimit;

  const { user } = useAuthStore();
  const manualDisciplineChangeRef = useRef(false);
  const [selectedHorse, setSelectedHorse] = useState(null);
  const [selectedHorseStatus, setHorseStatus] = useState(null);
  const [selectedSort, setSelectedSort] = useState(arrivalsOptions[0]);
  const [selectedSize, setSelectedSize] = useState([]);
  const [apiSelectedSizes, setApiSelectedSizes] = useState([]);

  /** GraphQL `SaddleFitFilter2.size` is `[Float]`; URL state uses strings. */
  const sizeFilterNumeric = useMemo(() => {
    if (!apiSelectedSizes?.length) return [];
    return apiSelectedSizes
      .map((s) =>
        typeof s === "number" ? s : parseFloat(String(s).trim(), 10)
      )
      .filter((n) => Number.isFinite(n));
  }, [apiSelectedSizes]);

  const updateQueryParams = (params) => {
    const searchParams = new URLSearchParams(location.search);

    Object.entries(params).forEach(([key, value]) => {
      if (value) {
        if (key === "size") {
          searchParams.delete(key);
          searchParams.append(key, value);
        } else {
          searchParams.set(key, value);
        }
      } else {
        searchParams.delete(key);
      }
    });

    const cleanQuery = searchParams.toString().replace(/%2C/g, ",");
    navigate({ search: cleanQuery }, { replace: true });
  };

  const mapSortValue = (value) => {
    const sortItems = [
      ...arrivalsOptions,
      { value: "BEST_SELLING", label: "Best Selling" },
    ];
    return sortItems.find((option) => option.value === value) || null;
  };

  const mapBrandValues = (value) => {
    return brandOptions.find((option) => option?.label === value) || null;
  };

  const { data: listUserHorsesData, refetch: refetchListUserHorses } = useQuery(
    gql(listUserHorses),
    { skip: !user }
  );

  const getSortOptions = (selectedArrival) => {
    if (!selectedArrival || selectedArrival?.value === "BEST_FIT_SCORE") {
      return null;
    }

    switch (selectedArrival?.value) {
      case "PRICE_LOW":
        return { name: "price", order: "ASC" };
      case "PRICE_HIGH":
        return { name: "price", order: "DESC" };
      case "NEW_ARRIVALS":
        return { name: "createdAt", order: "DESC" };
      default:
        return null;
    }
  };

  const { data: listDisciplinesData } = useQuery(
    gql(LIST_DISCIPLINES),
    LIST_DISCIPLINES_QUERY_OPTIONS
  );

  const [westernOptions, setWesternOptions] = useState([]);

  useEffect(() => {
    if (listDisciplinesData?.listDisciplines) {
      const formattedOptions = formatListArray(
        listDisciplinesData.listDisciplines
      );
      const filteredWesternOptions = formattedOptions.filter(
        (option) => String(option?.label ?? "").toUpperCase() === "WESTERN"
      );
      setWesternOptions(filteredWesternOptions);
    }
  }, [listDisciplinesData]);

  const mapDisciplineValue = (value) => {
    for (const discipline of westernOptions) {
      const foundOption = discipline.options.find(
        (option) => option.value === value
      );
      if (foundOption) {
        return foundOption;
      }
    }

    return null;
  };

  useEffect(() => {
    manualDisciplineChangeRef.current = false;
  }, [selectedHorse, listUserHorsesData, listDisciplinesData]);

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const disciplineValue = searchParams.get("discipline");
    const sortValue = searchParams.get("sort");
    const brandValue = searchParams.get("brand");
    const minPriceFromUrl =
      parseInt(searchParams.get("minPrice"), 10) || minPriceLimit;
    const maxPriceFromUrl =
      parseInt(searchParams.get("maxPrice"), 10) || maxPriceLimit;

    if (disciplineValue) {
      const disciplineValues = disciplineValue.split(",");
      const newDisciplines = disciplineValues
        .map((val) => mapDisciplineValue(val))
        .filter(Boolean);
      setSelectedDiscipline(newDisciplines);
    } else {
      setSelectedDiscipline(null);
    }

    const newSort = sortValue
      ? (mapSortValue(sortValue) ?? arrivalsOptions[0])
      : arrivalsOptions[0];
    setSelectedSort(newSort);

    const newBrandName = mapBrandValues(brandValue);
    setSelectedBrandName(newBrandName);

    setValue("currentMinPrice", `$${minPriceFromUrl.toLocaleString("en-US")}`);
    setValue("currentMaxPrice", `$${maxPriceFromUrl.toLocaleString("en-US")}`);
    setValue("minPrice", `$${minPriceFromUrl.toLocaleString("en-US")}`);
    setValue("maxPrice", `$${maxPriceFromUrl.toLocaleString("en-US")}`);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [westernOptions, setValue, location.search]);

  // TODO (meeting req): Fix horse filter - "filtering mechanism is not pulling up"
  // BUG: When horse has no discipline, this block is skipped and query params are never updated.
  // FIX: Handle horses without discipline - still set selectedHorse and update query params (horse, size).
  const byHorseFilterHandler = (selectedValue) => {
    if (selectedValue?.value === HORSE_SELECT_LOGIN_VALUE) {
      open(Modal.Login);
      setSelectedHorse(null);
      return;
    }
    if (selectedValue) {
      const selectedHorseData = listUserHorsesData?.listUserHorses?.items?.find(
        (item) => item?.id === selectedValue?.value
      );

      if (selectedHorseData?.discipline) {
        const result = getByDisciplineFilterValues(
          selectedHorseData?.discipline,
          listDisciplinesData?.listDisciplines
        );

        setHorseStatus(selectedHorseData?.status);
        setSelectedHorse(selectedValue);

        if (result && Array.isArray(result) && result.length > 0) {
          setSelectedDiscipline(result);
        } else {
          setSelectedDiscipline(null);
        }

        const userDefaultSize = user?.size || [];

        const sizeArray = userDefaultSize
          .map((val) =>
            seatSizes.find((size) => size.value.toString() === val.toString())
          )
          .filter(Boolean);
        setSelectedSize(sizeArray);
        setApiSelectedSizes(userDefaultSize.map((val) => val.toString()));

        const sizeString = userDefaultSize.join(",");

        updateQueryParams({
          size: sizeString,
          horse: selectedValue?.value || null,
          discipline: (result && result.length > 0 && result[0]?.value) || null,
        });
      }
    } else {
      updateQueryParams({ horse: null });
      setSelectedHorse(null);
    }
  };

  const byDisciplineFilterHandler = (selectedValues) => {
    manualDisciplineChangeRef.current = true;
    setSelectedDiscipline(selectedValues);
    updateQueryParams({
      discipline:
        selectedValues && selectedValues.length
          ? selectedValues.map((item) => item.value).join(",")
          : null,
    });
  };

  const bySortFilterHandler = (selectedValue) => {
    manualDisciplineChangeRef.current = true;
    setSelectedSort(selectedValue);

    if (selectedValue?.value === "BEST_FIT_SCORE") {
      setSelectedBrandName(null);
      setSelectedSize([]);
      setApiSelectedSizes([]);
      setSelectedDiscipline(null);

      updateQueryParams({
        sort: selectedValue?.value,
        brand: null,
        size: null,
        discipline: null,
      });
    } else {
      updateQueryParams({ sort: selectedValue?.value || null });
    }
  };

  const handleSizeChange = (selectedValues) => {
    manualDisciplineChangeRef.current = true;
    setSelectedSize(selectedValues);

    const cleanSizes = selectedValues.map((item) => item.value.toString());
    setApiSelectedSizes(cleanSizes);

    updateQueryParams({ size: cleanSizes.join(",") });
  };

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const sizeValue = searchParams.get("size");
    const userDefaultSize = user?.size;

    if (sizeValue) {
      const sizeArray = sizeValue
        .split(",")
        .map((val) => seatSizes.find((size) => size.value.toString() === val))
        .filter(Boolean);

      setSelectedSize(sizeArray);
      setApiSelectedSizes(sizeValue.split(","));

      methods.setValue("size", sizeValue);
    } else if (userDefaultSize && Array.isArray(userDefaultSize)) {
      const sizeArray = userDefaultSize
        .map((val) =>
          seatSizes.find((size) => size.value.toString() === val.toString())
        )
        .filter(Boolean);

      setSelectedSize(sizeArray);
      setApiSelectedSizes(userDefaultSize.map((val) => val.toString()));

      const sizeString = userDefaultSize.join(",");
      methods.setValue("size", sizeString);

      updateQueryParams({ size: sizeString });
    } else if (userDefaultSize) {
      const defaultSize = seatSizes.find(
        (size) => size.value.toString() === userDefaultSize.toString()
      );
      if (defaultSize) {
        setSelectedSize([defaultSize]);
        setApiSelectedSizes([defaultSize.value.toString()]);

        methods.setValue("size", defaultSize.value.toString());

        updateQueryParams({ size: defaultSize.value.toString() });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, methods]);

  const handleBrandChange = (selectedItem) => {
    manualDisciplineChangeRef.current = false;
    setSelectedBrandName(selectedItem);
    updateQueryParams({ brand: selectedItem?.label || null });
  };

  // useEffect(() => {
  //   if (user) {
  //     const ByHorseFilterOption = listUserHorsesData?.listUserHorses?.items.map(
  //       (item) => ({
  //         value: item?.id,
  //         label: item?.name,
  //       })
  //     );
  //     if (
  //       Array.isArray(ByHorseFilterOption) &&
  //       ByHorseFilterOption.length > 0
  //     ) {
  //       setSelectedHorse(ByHorseFilterOption[0]);
  //     }
  //   }
  // }, [listUserHorsesData, user]);

  useEffect(() => {
    if (user) {
      const fetchListUserHorsesData = async () => {
        await refetchListUserHorses();
      };
      fetchListUserHorsesData();
    }
  }, [user, refetchListUserHorses]);

  // Read ?horse= straight from the URL so scoring fires even if selectedHorse
  // hasn't resolved yet (it depends on listUserHorses landing first).
  const horseIdFromUrl = new URLSearchParams(location.search).get("horse");

  const { data, loading, fetchMore } = useQuery(gql(GET_SADDLES_QUERY), {
    variables: {
      limit: 6,
      filter: {
        ...(selectedBrandName?.name && {
          brandName: selectedBrandName.name,
        }),
        ...(subdomain && {
          brandName: brandName,
        }),
        ...(isCactus && { brandName: "cactussaddlery" }),
        ...(isBillyCook && { brandName: "billycook" }),
        ...(selectedDiscipline &&
          selectedDiscipline.length > 0 && {
          discipline: selectedDiscipline.map((item) => item.value),
        }),
        ...(sizeFilterNumeric.length > 0 && { size: sizeFilterNumeric }),
      },
      fromPrice: minPrice,
      toPrice: maxPrice,
      horseId: selectedHorse?.value || horseIdFromUrl || null,
      sort: getSortOptions(selectedSort),
    },
    fetchPolicy: "network-only",
  });

  const [isLoadingMore, setIsLoadingMore] = useState(false);

  const loadMoreSaddles = () => {
    if (!data?.listSaddles?.nextToken) return;

    setIsLoadingMore(true);

    fetchMore({
      variables: {
        limit: 6,
        nextToken: data?.listSaddles?.nextToken,
      },
      updateQuery: (prev, { fetchMoreResult }) => {
        setIsLoadingMore(false);

        if (!fetchMoreResult) return prev;

        return {
          listSaddles: {
            ...fetchMoreResult.listSaddles,
            items: [
              ...prev.listSaddles.items,
              ...fetchMoreResult.listSaddles.items,
            ],
          },
        };
      },
    });
  };

  const [loadingFavorites, setLoadingFavorites] = useState(
    Array(data?.listHorseSaddleFit?.items.length).fill(false)
  );

  const [createSaddleFavourite] = useMutation(gql(CREATE_SADDLE_FAVORITE), {
    refetchQueries: ["listSaddleFavorites"],
  });

  const [deleteSaddleFavourite] = useMutation(gql(DELETE_SADDLE_FAVORITE), {
    refetchQueries: ["listSaddleFavorites"],
  });

  const { data: favouriteSaddleData } = useQuery(gql(LIST_SADDLE_FAVORITES), {
    skip: !user,
    fetchPolicy: "network-only",
  });

  const favouriteSaddleList = favouriteSaddleData?.listSaddleFavorites?.map(
    (item) => item.saddleId
  );

  useEffect(() => {
    if (selectedHorse && !loading && cardsRef?.current) {
      const isMobile = window.innerWidth <= 768;

      setTimeout(() => {
        if (isMobile) {
          ref?.current?.scrollIntoView({ behavior: "smooth" });
        } else {
          cardsRef?.current?.scrollIntoView({
            behavior: "smooth",
            block: "center",
          });
        }
      }, 100);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, selectedHorse]);

  const handleFavourite = async (item, i) => {
    const updatedLoadingFavorites = [...loadingFavorites];
    updatedLoadingFavorites[i] = true;
    setLoadingFavorites(updatedLoadingFavorites);

    try {
      if (favouriteSaddleList?.includes(item?.sk)) {
        await deleteSaddleFavourite({
          variables: {
            saddleId: item?.sk,
          },
        });
      } else {
        await createSaddleFavourite({
          variables: {
            input: {
              saddleId: item?.sk,
              horseId: selectedHorse?.value,
              score: item?.score,
              brandName: item?.brandName,
            },
          },
        });
      }
    } catch (error) {
      updatedLoadingFavorites[i] = false;
      setLoadingFavorites(updatedLoadingFavorites);
      ModalStore.openSimpleError({
        icon: undefined,
        title: "Error",
        subtitle: error?.message,
        shouldCloseOnOverlayClick: false,
      });
    } finally {
      updatedLoadingFavorites[i] = false;
      setLoadingFavorites(updatedLoadingFavorites);
    }
  };

  // TODO (meeting req): ByHorseFilterOption can be undefined when items not yet loaded
  // (count > 0 but items empty). Modal opens with empty dropdown - ensure options exist before opening.
  const ByHorseFilterOption = useMemo(
    () =>
      listUserHorsesData?.listUserHorses?.items?.map((item) => ({
        value: item?.id,
        label: item?.name,
      })) ?? [],
    [listUserHorsesData]
  );

  const horseDropdownOptions = useMemo(() => {
    if (!user) {
      return [
        {
          value: HORSE_SELECT_LOGIN_VALUE,
          label: "Log in to select your horse",
        },
      ];
    }
    return ByHorseFilterOption;
  }, [user, ByHorseFilterOption]);

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const horse = searchParams.get("horse");
    // TODO: Add !ByHorseFilterOption?.length check - don't open modal if no horses to select
    if (!horse && listUserHorsesData?.listUserHorses?.count > 0) {
      ModalStore.openSimpleSuccess({
        showHeader: false,
        fontSize: "40px",
        icon: undefined,
        title: "please select a horse!",
        children: (
          <SingleSelectField
            height="56px"
            className="cactus_formInput cursor-pointer"
            placeholder="Select a horse"
            isClearable={false}
            value={selectedHorse}
            options={horseDropdownOptions}
            onChange={byHorseFilterHandler}
            stylesOverride={{
              dropdownIndicator: (base) => ({ ...base, marginBottom: "0px" }),
              placeholder: (base) => ({ ...base, paddingTop: "15px" }),
              clearIndicator: (base) => ({ ...base, marginBottom: "0px" }),
              singleValue: (base) => ({
                ...base,
                marginTop: "15px !important",
              }),
            }}
          />
        ),
      });
    } else if (horse && ByHorseFilterOption) {
      const selectedHorseOption = ByHorseFilterOption.find(
        (option) => option.value === horse
      );
      setSelectedHorse(selectedHorseOption);

      const selectedHorseData = listUserHorsesData?.listUserHorses?.items?.find(
        (item) => item?.id === selectedHorseOption?.value
      );
      setHorseStatus(selectedHorseData?.status);
    } else {
      setSelectedHorse(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listUserHorsesData]);

  const generateSeoUrl = (item) => {
    const slug = item?.productTitle?.replace(/\s+/g, "-").toLowerCase();
    const year = new Date(item?.createdAt).getFullYear();
    return `/saddle/${slug}-${year}`;
  };

  const NavigateToDetailPage = (item, i) => {
    document.documentElement.style.scrollBehavior = "none";

    window.scrollTo(0, 0);
    navigate(generateSeoUrl(item), {
      state: { item, index: i, selectedHorse },
    });

    setTimeout(() => {
      document.documentElement.style.scrollBehavior = "";
    }, 0);
  };

  const sortBySaddleFitScore = (saddles) => {
    if (!saddles) return [];
    return [...saddles].sort((a, b) => (b.score || 0) - (a.score || 0));
  };

  const listSaddlesCactus = data?.listSaddles?.items;
  const displayedSaddles =
    selectedSort?.value === "BEST_FIT_SCORE"
      ? sortBySaddleFitScore(listSaddlesCactus)
      : listSaddlesCactus;

  const minInputRef = useRef(null);
  const maxInputRef = useRef(null);

  const updateQueryParamsSlider = (newMinPrice, newMaxPrice) => {
    const searchParams = new URLSearchParams(location.search);
    const finalMin = newMinPrice !== undefined ? newMinPrice : minPrice;
    const finalMax = newMaxPrice !== undefined ? newMaxPrice : maxPrice;
    searchParams.set("minPrice", finalMin);
    searchParams.set("maxPrice", finalMax);
    navigate({ search: searchParams.toString() }, { replace: true });
  };

  return (
    <>
      <div
        ref={ref}
        className="flex flex-col justify-center items-center mt-[40px] sm:mt-[61px]"
      >
        <div className="flex flex-col justify-center items-center gap-y-[34px]">
          <p className="cactus_main_heading">
            Select Your Horse to Get{" "}
            <span>
              Your {subdomain ? formatName(subdomain) : "SaddleFit"} Score
            </span>
          </p>
          <SingleSelectField
            height="56px"
            className="cactus_formInput cursor-pointer"
            placeholder="Select a horse"
            // isClearable={false}
            value={selectedHorse}
            options={horseDropdownOptions}
            onChange={byHorseFilterHandler}
            stylesOverride={{
              dropdownIndicator: (base) => ({ ...base, marginBottom: "0px" }),
              placeholder: (base) => ({ ...base, paddingTop: "15px" }),
              clearIndicator: (base) => ({ ...base, marginBottom: "0px" }),
              singleValue: (base) => ({
                ...base,
                marginTop: "15px !important",
              }),
            }}
          />
        </div>
        <div className="gap-y-[34px] mt-[54px] flex flex-col justify-center items-center">
          <h4 className="best_selling_saddle">Find Your Best Fitting Saddle</h4>
          <div className="flex flex-col md:flex-row gap-y-[34px] md:gap-x-[34px]">
            <div className="flex flex-col items-center justify-center gap-y-6 p-4 max-w-[357px] sm:max-w-[361px] border border-solid border-[#EAEBED] rounded-2xl">
              <h2 className="text-[#2B364B] text-base font-[Montserrat] font-bold leading-normal">
                Pricing
              </h2>
              <FormProvider {...methods}>
                <div className="flex flex-row items-center gap-x-4">
                  <FormInput
                    name="currentMinPrice"
                    className="w-[156.5px] form_input_min_val !h-auto relative"
                    label="Min Price"
                    maxLength={6}
                    inputRef={minInputRef}
                    onChange={(e) => {
                      const inputElem = e.target;
                      let caret = inputElem.selectionStart;
                      let inputValue = inputElem.value;

                      if (!inputValue.startsWith("$")) {
                        inputValue = "$" + inputValue;
                        caret++;
                      }
                      if (inputValue.length > 6) {
                        inputValue = inputValue.slice(0, 6);
                      }

                      setValue("currentMinPrice", inputValue);

                      const numericStr = inputValue
                        .replace(/\$/g, "")
                        .replace(/,/g, "");
                      if (!numericStr) return;

                      const numericValue = parseInt(numericStr, 10);
                      if (numericValue >= 1500 && numericValue <= 9000) {
                        // If the new min is greater than the current max, not updating anything.
                        if (numericValue > maxPrice) {
                          return;
                        }

                        const formattedValue =
                          "$" + numericValue.toLocaleString("en-US");
                        updateQueryParamsSlider(numericValue, maxPrice);
                        setValue("minPrice", formattedValue);
                        setValue("currentMinPrice", formattedValue);

                        const oldCommaCount = (inputValue.match(/,/g) || [])
                          .length;
                        const newCommaCount = (formattedValue.match(/,/g) || [])
                          .length;
                        const diff = newCommaCount - oldCommaCount;
                        const newCaret = caret + diff;
                        setTimeout(() => {
                          if (minInputRef.current) {
                            minInputRef.current.setSelectionRange(
                              newCaret,
                              newCaret
                            );
                          }
                        }, 0);
                      }
                    }}
                  />

                  <FormInput
                    name="currentMaxPrice"
                    className="w-[156.5px] form_input_max_val !h-auto relative"
                    label="Max Price"
                    maxLength={6}
                    inputRef={maxInputRef}
                    onChange={(e) => {
                      const inputElem = e.target;
                      let caret = inputElem.selectionStart;
                      let inputValue = inputElem.value;

                      if (!inputValue.startsWith("$")) {
                        inputValue = "$" + inputValue;
                        caret++;
                      }
                      if (inputValue.length > 6) {
                        inputValue = inputValue.slice(0, 6);
                      }

                      setValue("currentMaxPrice", inputValue);

                      const numericStr = inputValue
                        .replace(/\$/g, "")
                        .replace(/,/g, "");
                      if (!numericStr) return;

                      const numericValue = parseInt(numericStr, 10);
                      if (numericValue >= 2500 && numericValue <= 10000) {
                        // NEW: new max is not below the current min.
                        if (numericValue < minPrice) {
                          return;
                        }
                        const formattedValue =
                          "$" + numericValue.toLocaleString("en-US");
                        updateQueryParamsSlider(minPrice, numericValue);
                        setValue("maxPrice", formattedValue);
                        setValue("currentMaxPrice", formattedValue);

                        const oldCommaCount = (inputValue.match(/,/g) || [])
                          .length;
                        const newCommaCount = (formattedValue.match(/,/g) || [])
                          .length;
                        const diff = newCommaCount - oldCommaCount;
                        const newCaret = caret + diff;
                        setTimeout(() => {
                          if (maxInputRef.current) {
                            maxInputRef.current.setSelectionRange(
                              newCaret,
                              newCaret
                            );
                          }
                        }, 0);
                      }
                    }}
                  />
                </div>

                <RangeSlider
                  isCactus={isCactus}
                  minPrice={parseInt(minPrice)}
                  maxPrice={parseInt(maxPrice)}
                  setMinPrice={(value) =>
                    setValue("minPrice", `$${value.toLocaleString("en-US")}`)
                  }
                  setMaxPrice={(value) =>
                    setValue("maxPrice", `$${value.toLocaleString("en-US")}`)
                  }
                />
              </FormProvider>
            </div>

            <div className="flex flex-col items-center gap-8 h-auto">
              <SingleSelectField
                label="Sort"
                height="44px"
                value={selectedSort}
                options={arrivalsOptions}
                onChange={bySortFilterHandler}
                placeholder={`Best Fit Score`}
                className="!w-[300px] md:!w-[295px]"
              />
              {/* Brand filter - hidden on manufacturer landing pages */}
              {!isExclusiveBrandPage && (
                <SingleSelectField
                  label="Brand"
                  height="44px"
                  options={brandOptions}
                  value={selectedBrandName}
                  placeholder="Select Brand"
                  className="!w-[300px] md:!w-[295px]"
                  onChange={(item) => handleBrandChange(item)}
                />
              )}
              {/* TODO (meeting req): Consider removing - "only processing Western saddles now the discipline" */}
              <MultiSelectField
                label="Type"
                height="44px"
                options={westernOptions}
                value={selectedDiscipline}
                placeholder="Select Discipline"
                className="!w-[300px] md:!w-[295px]"
                onChange={(item) => byDisciplineFilterHandler(item)}
                stylesOverride={{
                  valueContainer: (base) => ({
                    ...base,
                    padding: 0,
                    paddingLeft: 8,
                  }),
                  dropdownIndicator: (base) => ({ ...base, marginBottom: 0 }),
                  clearIndicator: (base) => ({ ...base, marginBottom: 0 }),
                }}
              />
              <MultiSelectField
                height="44px"
                label="Seat Size"
                options={seatSizes}
                value={selectedSize}
                placeholder="Select Size"
                onChange={handleSizeChange}
                className="!w-[300px] md:!w-[295px]"
                stylesOverride={{
                  valueContainer: (base) => ({
                    ...base,
                    padding: 0,
                    paddingLeft: 8,
                  }),
                  dropdownIndicator: (base) => ({ ...base, marginBottom: 0 }),
                  clearIndicator: (base) => ({ ...base, marginBottom: 0 }),
                }}
              />
            </div>
          </div>

          <div className="cactus_notify">
            <CactusNotifyIcon />
            <p className="saddle_note">
              <span className="pr-1">NOTE:</span> Saddles with this
            </p>
            <div className="flex gap-x-1">
              <span className="dots">“</span>
              <CactusSaddleIcon />
              <span className="dots">”</span>
            </div>
            <p className="cactus_notify_desc">
              icon are the top fitting saddles for your horse!
            </p>
          </div>
        </div>
      </div>
      {loading ? (
        <div className="flex justify-center items-center h-[100px] w-full">
          <Loader
            filledColor="#2b364b"
            className="!h-[50px] !flex !items-center !text-center !justify-center"
          />
        </div>
      ) : (
        <>
          <CarouselCards
            ref={cardsRef}
            isCactus={isCactus}
            isBillyCook={isBillyCook}
            items={displayedSaddles}
            selectedHorse={selectedHorse?.value}
            selectedHorseStatus={selectedHorseStatus}
            horses={ByHorseFilterOption}
            loading={loading}
            addToCartLoading={addToCartLoading}
            loadingFavorites={loadingFavorites}
            favouriteSaddleList={favouriteSaddleList}
            unAuthDetailsClick={(item, index) => {
              NavigateToDetailPage(item, index);
            }}
            onMoreDetailsClick={(item, index) => {
              if (selectedSize || selectedHorse?.value) {
                NavigateToDetailPage(item, index);
              } else if (user && !selectedHorse?.value) {
                ModalStore.openSimpleSuccess({
                  icon: undefined,
                  title: "No horse profile found!",
                  shouldCloseOnOverlayClick: false,
                  children: (
                    <Button
                      onClick={() => {
                        navigate("/user/horses");
                        ModalStore.closeAll();
                      }}
                      className="add_horse_btn"
                    >
                      Add Horse Profile
                    </Button>
                  ),
                });
              } else if (user && !selectedSize) {
                ModalStore.openSimpleSuccess({
                  icon: undefined,
                  title: "Choose Your Seat Size!",
                  subtitle: "Please select a seat size!",
                  shouldCloseOnOverlayClick: false,
                });
              }
            }}
            onAddToCartClick={(item) => {
              if (user && !selectedHorse?.value) {
                const horseCount =
                  listUserHorsesData?.listUserHorses?.count || 0;
                if (horseCount > 0) {
                  ModalStore.openSimpleSuccess({
                    showHeader: false,
                    fontSize: "40px",
                    icon: undefined,
                    title: "please select a horse!",
                    children: (
                      <SingleSelectField
                        height="56px"
                        className="cactus_formInput cursor-pointer"
                        placeholder="Select a horse"
                        isClearable={false}
                        value={selectedHorse}
                        options={horseDropdownOptions}
                        onChange={byHorseFilterHandler}
                        stylesOverride={{
                          dropdownIndicator: (base) => ({
                            ...base,
                            marginBottom: "0px",
                          }),
                          placeholder: (base) => ({
                            ...base,
                            paddingTop: "15px",
                          }),
                          clearIndicator: (base) => ({
                            ...base,
                            marginBottom: "0px",
                          }),
                          singleValue: (base) => ({
                            ...base,
                            marginTop: "15px !important",
                          }),
                        }}
                      />
                    ),
                  });
                } else {
                  ModalStore.openSimpleSuccess({
                    icon: undefined,
                    title: "No horse profile found!",
                    shouldCloseOnOverlayClick: false,
                    children: (
                      <Button
                        onClick={() => {
                          navigate("/user/horses");
                          ModalStore.closeAll();
                        }}
                        className="add_horse_btn"
                      >
                        Add Horse Profile
                      </Button>
                    ),
                  });
                }
              } else if (user && !selectedSize) {
                ModalStore.openSimpleSuccess({
                  icon: undefined,
                  title: "Choose Your Seat Size!",
                  subtitle: "Please select a seat size!",
                  shouldCloseOnOverlayClick: false,
                });
              } else {
                handleAddToCart(item, selectedHorse, changeActionState, open);
              }
            }}
            onFavouriteClick={(item, index) => {
              handleFavourite(item, index);
            }}
          />
          {(user || isExclusiveBrandPage) && data?.listSaddles?.nextToken && (
            <div className="flex justify-center mx-auto">
              <Button
                className="load_more_btn mb-[30px]"
                primary
                whiteOutline
                loading={isLoadingMore}
                onClick={loadMoreSaddles}
              >
                load more
              </Button>
            </div>
          )}
        </>
      )}
    </>
  );
});

export default SaddleSelectionControls;
